<template>
    <div class="w-full flex flex-col gap-4">
        <div class="font-semibold">
            {{ $t('checkout.shipping.headline') }}
        </div>

        <Transition name="fade" mode="out-in">
            <div v-if="loading" class="flex flex-col gap-4">
                <div class="flex gap-4">
                    <MiscSkeleton circle size="large" />
                    <MiscSkeleton text size="large" />
                </div>
            </div>

            <div v-else-if="error">
                {{ error }}
            </div>

            <div v-else-if="shippingMethods != null" class="flex flex-col gap-4">
                <label
                    v-for="method in shippingMethods"
                    :key="method.id"
                    :for="'shipping-option-' + method.id"
                    class="flex justify-between items-start gap-4 rounded-xs "
                    :class="{ 'cursor-pointer': shippingMethods.length > 1 }"
                >
                    <input
                        v-show="shippingMethods.length > 1"
                        :id="'shipping-option-' + method.id"
                        v-model="selectedMethodId"
                        :value="method.id"
                        type="radio"
                        class="radio checked:bg-primary w-6"
                        :disabled="updateLoading"
                    >
                    <span class="mr-auto flex flex-col gap-2 text-sm">
                        <span class="font-semibold">{{ method?.translated?.name }}</span>
                        <span v-if="method?.translated?.description != null" class="text-base-content-subtle">{{ method?.translated?.description }}</span>
                    </span>
                    <NuxtImg
                        v-if="method.media != null"
                        class="max-w-[70px] lg:max-w-[90px]"
                        :src="method.media?.url"
                        :alt="method.media?.translated?.alt ?? method.media?.alt"
                    />
                </label>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">

const emit = defineEmits<{
    (event: 'update-before:shippingMethod' | 'update-after:shippingMethod', id: string): void
}>()

const { showNotification } = useNotification()
const { t } = useI18n()
const {
    shippingMethods,
    getShippingMethods
} = useCheckout()
const {
    selectedShippingMethod: contextShippingMethod,
    setShippingMethod,
} = useSessionContext()

defineProps({
    currentStep: {
        type: String,
        default: ''
    }
})

// State
const loading = ref(false)
const updateLoading = ref(false)
const error = ref<string | null>(null)

// Computed
const selectedMethodId = computed({
    get(): string {
        return contextShippingMethod.value?.id || ''
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    set(methodId: string) {
        // This setter is mainly for form binding, actual logic is in the watcher
    }
})

watch(selectedMethodId, async (value, oldValue) => {
    if (value !== oldValue && value !== null) {
        error.value = null
        updateLoading.value = true
        emit('update-before:shippingMethod', value)

        try {
            await setShippingMethod({ id: value })
            emit('update-after:shippingMethod', value)
        } catch (err) {
            error.value = err instanceof Error ? err.message : t('checkout.shipping.updateError')
            showNotification(error.value, 'error')
        } finally {
            updateLoading.value = false
        }
    }
})

onMounted(async () => {
    error.value = null
    loading.value = true

    try {
        await getShippingMethods()
    } catch (err) {
        error.value = err instanceof Error ? err.message : t('checkout.shipping.loadError')
        showNotification(error.value, 'error')
    } finally {
        loading.value = false
    }
})
</script>
